<!--
  TODO (cleanup): Think about creating a reusable easy component for handling similar logic of "Add Dialog" or "Edit Dialog".
-->

<template>
  <Dialog ref="dialog" panelClass="bui-dialog-panel">
    <template #expander="{ openModal }">
      <!-- Add Binding Mode -->
      <template v-if="props.mode === 'add'">
        <!-- Add Binding Button -->
        <ButtonCustomized
          v-if="props.mode === 'add'"
          label="Add Binding"
          data-testid="subscription-add-binding"
          @click="handleClickNewBinding"
          style="width: fit-content;"
          :disabled="sideState.isSideFeatureSubscription"
        />
        <!--
          TODO: consider allowing adding additional bindings to Side-Feature Subscription
            Use case: A user wants to add a slider to a subscription that has a Live Stream Binding
         -->
        <TooltipMessage
          v-if="sideState.isSideFeatureSubscription"
          class="ml-2"
          message="Adding additional bindings to a Side-Feature Subscription (e.g. Live Stream) is not allowed!"
        />
      </template>
      <!-- Edit Binding Mode -->
      <template v-else>
        <!-- Edit Binding Button -->
        <button
          type="button"
          class="pe-row-action-btn"
          aria-label="Edit binding"
          @click="sideState.isSideFeatureSubscription
            ? routeToSideFeatureEditPage()
            : handleClickEditBinding()
          "
        >
          <PhPencilSimple :size="16" weight="bold" />
        </button>
      </template>
    </template>
    <template #dialog.title>{{ props.mode === 'add' ? 'Add New Binding' : 'Edit Binding' }}</template>
    <template #dialog.content="{ closeModal, closeModalAfter }">
      <div class="form-container">
        <SelectField
          label="Integration Instance"
          empty-first-option="Select Integration Instance"
          :options="sideState.integrationInstances"
          v-model="selectedInstanceId"
          label-key="name"
          value-key="id"
          test-id="binding-integration-instance"
        />
        <div class="binding-form-container">
          <!-- Debugging Helper -->
          <!-- <pre class="overflow-y-scroll h-60">{{ {
            bindingIdAtOpening,
            implementationKeyAtOpening,
            bindingData,
            instanceConfigValues: selectedInstance?.configValues,
            selectedInstance
          } }}</pre>
          <pre class="overflow-y-scroll h-60">{{ {
            selectedInstanceId,
            bindingData,
            modelValue
          } }}</pre> -->

          <!-- Binding Form -->
          <KeepAlive>
            <component
              v-if="selectedImplementation?.BindingFormComponent"
              :is="selectedImplementation?.BindingFormComponent"
              :instance="selectedInstance"
              :key="selectedInstanceId"
              v-model="bindingData"
            />
            <div v-else-if="!selectedImplementationKey">Select Integration Instance to setup binding!</div>
            <div v-else>Integration Instance not supported yet!</div>
          </KeepAlive>
          <div class="form-buttons">
            <ButtonCustomized label="Cancel" data-testid="subscription-binding-cancel" @click="closeModal" ctaSecondary />
            <ButtonCustomized
              :label="props.mode === 'add' ? 'Add Binding' : 'Update Binding'"
              data-testid="subscription-binding-save"
              cta
              @click="onSave"
            />
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import Dialog from '@/components/Dialog/Dialog.vue';
import ButtonCustomized from "@/components/form-fields/ButtonCustomized.vue";
import SelectField from '@/components/form-fields/SelectField/SelectField.vue';
import TooltipMessage from '@/components/form-fields/TooltipMessage.vue';
import { PhPencilSimple } from '@phosphor-icons/vue';
import { ref, defineAsyncComponent, computed, watch } from 'vue';
import { usePlayerSubscription } from '../composables/playerSubscription';
import { clearReactiveObject } from '@/helpers/utils';
const { sideState, getHumanReadableInfo, routeToSideFeatureEditPage } = usePlayerSubscription();

const props = withDefaults(defineProps<{
  mode: "add" | "edit";
  modelValue?: any;
}>(), {
  mode: 'add',
  modelValue: () => ({}),
});
const emit = defineEmits(['cancel', 'add', 'update:modelValue', 'exit', 'fail'])

const dialog = ref<InstanceType<typeof Dialog>>();

const bindingData = ref(props.modelValue || {} as any);
// console.log('bindingData from Binding Dialog', bindingData.value); // Debugging Helper
const bindingIdAtOpening = ref();
const implementationKeyAtOpening = ref();

// Important to re-initialize data when dialog is opened with edit mode
const isInitiated = ref(false);

const selectedInstanceId = ref(bindingData.value?.integrationInstanceId);
const selectedInstance = computed(() => 
  sideState.integrationInstances
    .find((i: any) => String(i.id) === String(selectedInstanceId.value))
);
const selectedImplementationKey = computed<keyof typeof implementationsMapping>(() => selectedInstance.value?.implementationKey);
const selectedImplementation = computed(() => implementationsMapping[selectedImplementationKey.value]);

watch(selectedInstanceId, () => {
  // Important to reset bindingData when different instances are selected
  if(!isInitiated.value) return;
  clearReactiveObject(bindingData.value);
})

// TODO (cleanup): separate this file into a map config file
/** Integration Implementations Map that maps `implementationKey` to `name`, `Binding Form component` */
const implementationsMapping = {
  'qmatic': {
    name: "Qmatic",
    BindingFormComponent: defineAsyncComponent(() => import("./BindingForms/Qmatic/QmaticBindingForm.vue")),
  },
  'weatherApi': {
    name: "Weather API",
    BindingFormComponent: defineAsyncComponent(() => import("./BindingForms/WeatherApi/WeatherApi.vue")),
  },
  'square': {
    name: "Square",
    BindingFormComponent: defineAsyncComponent(() => import("./BindingForms/Square/SquareBindingForm.vue")),
  }
}

function onSave() {
  /**
   * Keep binding id the same as the opening binding id if the on saving implementation
   * key is the same as the opening implementation key. Otherwise, set the binding id
   * to null.
   */
  const implementationKey = sideState.integrationInstances.find((i: any) =>
    String(i.id) === String(selectedInstanceId.value))?.implementationKey;
  if(implementationKey === implementationKeyAtOpening.value) {
    if(bindingIdAtOpening.value) {
      bindingData.value.id = bindingIdAtOpening.value;
    }
  } else {
    bindingData.value.id = undefined;
  }

  const bindingPrepared = prepareBindingData(bindingData.value, selectedInstanceId.value);
  if(props.mode === 'add') {
    // Add binding mode
    emit('add', bindingPrepared);
  } else {
    // Update binding mode
    Object.assign(bindingData.value, bindingPrepared);
    emit('update:modelValue', bindingPrepared);
  }
  dialog.value?.closeModal();
}

function prepareBindingData(bindingData: object, selectedInstanceId: string) {
  const baseData = {
    integrationInstanceId: selectedInstanceId,
    ...bindingData,
  }
  const bindingPrepared = {
    ...baseData,
    humanReadableInfo: getHumanReadableInfo(baseData, selectedInstance.value),
  }
  return bindingPrepared;
}

function handleClickNewBinding() {
  // Reset state
  bindingData.value = {};
  selectedInstanceId.value = null;
  
  // Open modal
  dialog.value?.openModal();
}

function handleClickEditBinding() {
  // Prepare binding id and implementation key at opening
  bindingIdAtOpening.value = props.modelValue?.id;
  implementationKeyAtOpening.value = sideState.integrationInstances.find((i: any) =>
    String(i.id) === String(props.modelValue?.integrationInstanceId))?.implementationKey;
  
  isInitiated.value = false;
  // Set selected instance to the binding's instance
  selectedInstanceId.value = props.modelValue.integrationInstanceId;
  const bindingPrepared = prepareBindingData(props.modelValue, selectedInstanceId.value);
  Object.assign(bindingData.value, bindingPrepared);
  // Important to set in timeout to queue it after the already queued watch "selectedInstanceId" change callback
  setTimeout(() => {
    isInitiated.value = true;
  })

  // Open modal
  dialog.value?.openModal();
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

.form-container {
  display: flex;
  flex-direction: column;
  max-width: 30rem;
  color: var(--bui-tbl-row-text, #2a2969);

  /* Generic token treatment for the async BindingForms (Qmatic / Weather /
     Square) without editing each form: labels, inputs and selects pick up
     BUI input tokens; checkboxes/radios get the checked accent. */
  :deep(label),
  :deep(.label) {
    color: var(--bui-tbl-row-text, #2a2a30);
  }

  :deep(input[type='text']),
  :deep(input[type='number']),
  :deep(textarea) {
    color: var(--bui-ter-input-text, #1a1a1e);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
  }

  :deep(input[type='checkbox']),
  :deep(input[type='radio']) {
    accent-color: var(--bui-chk-checked-fill, #de3da9);
  }

  :deep(.styled-select) {
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    border-radius: var(--bui-ter-radius, 8px);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));

    select {
      color: var(--bui-ter-input-text, #1a1a1e);
    }
  }
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.binding-form-container {
  padding: 1rem 0;
}
</style>